import type { Maturity } from '@tunasong/models'
import { shortUuid } from '@tunasong/models'
import type { TunaEditor, TunaEditorConfig, TunaPlugin } from '@tunasong/plugin-lib'
import { maturityFilter, useGlobalAppContext, usePlugins } from '@tunasong/plugin-lib'
import type { RootState } from '@tunasong/redux'
import { useThunkDispatch } from '@tunasong/redux'
import type { CoreElement, EntityOrElement } from '@tunasong/schemas'
import { isCoreElement } from '@tunasong/schemas'
import { useUserConfig } from '@tunasong/ui-lib'
import type { PlatePlugin } from '@platejs/core/react'
import { createPlateEditor, createPlatePlugin, withPlate } from '@platejs/core/react'
import { useEffect, useMemo, useState } from 'react'
import { useStore } from 'react-redux'

/** Create the (raw) editor. If you manage this you need to handle the root properties (e.g., id) separately. In the normal case, use `useCreateEditor` instead */

export const createTunaEditor = <T extends TunaEditor = TunaEditor>(
  id: string,
  plugins: TunaPlugin[],
  config: TunaEditorConfig,
  maturity: Maturity
) => {
  const ed = createPlateEditor<CoreElement[]>()

  /** corePlugins are included with Plate, filtered by maturity */
  const filteredPlugins = plugins.filter(maturityFilter(maturity))

  /** @todo do this in the plugins instead? */
  const preparePlugin = (p: TunaPlugin) => {
    const subPlugins = p.plugins?.map(p => createPlatePlugin(p as unknown as PlatePlugin)) ?? []
    return createPlatePlugin({ ...p, plugins: subPlugins } as unknown as PlatePlugin)
  }
  const platePlugins = filteredPlugins.map(preparePlugin)

  const editor = withPlate(ed, {
    id,
    plugins: platePlugins,
    nodeId: {
      idCreator: shortUuid,
      /**
       * Allow us to control the ids by explicitly setting it, otherwise it will be overwritten.
       * We need this for e.g., the `ref` plugin.
       */
      reuseId: true,
    },
  }) as unknown as T

  editor.config = config
  editor.setAwareness = (awareness: unknown) => {
    editor.awareness = awareness
  }
  editor.setPluginList = (plugins: TunaPlugin[]) => {
    editor.meta.pluginList = plugins as never
  }
  /** @todo move to using the Plate store for this */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editor.setProperty = (key: any, value: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(editor as any)[key] = value
  }

  return editor
}

// Create a new editor. It will re-create if rootElement.id or plugins change.
// When element is null, the rootElement is not set. We use this for "fake" editors.
export const useCreateEditor = (element: EntityOrElement | null, plugins: TunaPlugin[] | null) => {
  const dispatch = useThunkDispatch()
  const { getState } = useStore<RootState>()

  const { graph } = useGlobalAppContext()

  /** We allow only once creation per rootElement */
  const [rootElement, setRootElement] = useState<EntityOrElement | null>(element)

  useEffect(() => {
    if (!rootElement && element) {
      setRootElement(element)
    }
  }, [element, rootElement])

  const [maturity] = useUserConfig('featureMaturity')

  const editor = useMemo(() => {
    if (!plugins) {
      return null
    }
    const id = shortUuid()
    const editor = createTunaEditor(id, plugins, { dispatch, getState, graph }, maturity)

    if (isCoreElement(rootElement)) {
      editor.rootElement = rootElement
      editor.children = rootElement.children ?? []

      editor.tf.normalize({ force: true })
    }
    return editor
  }, [dispatch, getState, maturity, plugins, rootElement, graph])

  return editor
}

/**
 * Create a new editor dynamically when either the element or plugins change.
 * This is useful for cases where the editor needs to be re-created when either changes,
 * e.g., when rendering offscreen editors or previews.
 */
export const useCreateEditorDynamic = (
  element: EntityOrElement | null,
  providedPlugins: TunaPlugin[] | null = null
) => {
  const allPlugins = usePlugins('all')
  const plugins = providedPlugins ?? allPlugins
  const dispatch = useThunkDispatch()
  const { getState } = useStore<RootState>()

  const { graph } = useGlobalAppContext()

  const [editor, setEditor] = useState<TunaEditor | null>(null)

  const [maturity] = useUserConfig('featureMaturity')

  useEffect(() => {
    if (!plugins) {
      setEditor(null)
      return
    }
    const id = shortUuid()
    const editor = createTunaEditor(id, plugins, { dispatch, getState, graph }, maturity)

    if (isCoreElement(element)) {
      editor.rootElement = element
      editor.children = element.children ?? []

      editor.tf.normalize({ force: true })
    }
    setEditor(editor)
  }, [dispatch, element, getState, graph, maturity, plugins])

  return editor
}
